<script setup lang="ts">
import { formatting, Models, useLocalization, useOrderViewContext } from '@hantera/portal-app';
import Decimal from 'decimal.js';
import { computed } from 'vue';

const { state } = useOrderViewContext();
const t = useLocalization('components.order.financeOverview');

const productTotal = computed(() =>
  state.deliveries
    .flatMap(r => r.orderLines)
    .reduce((acc, line) => acc.add(line.orderLineTotal), new Decimal(0))
);

const shippingTotal = computed(() =>
  state.deliveries
    .reduce((acc, line) => acc.add(line.shippingTotal), new Decimal(0))
);

const taxLabel = computed(() => {
  return state.invoiceRecipient.countryCode === 'US' ? t('tax') : t('vat');
});

const displayTotal = computed(() =>
  state.taxIncluded ? state.orderTotal : state.orderTotal.add(state.orderTaxTotal)
);

const authorizedAmount = computed(() => {
  return state.payments.reduce((acc, payment) => acc.add(payment.authorizedAmount), new Decimal(0));
});

const invoicedAmount = computed(() => {
  return state.invoices.reduce((acc, invoice) => acc.add(invoice.invoiceTotal), new Decimal(0));
});

const capturedAmount = computed(() => {
  return state.journalEntries.filter(r => r.orderJournalEntryType === 'capture').reduce((acc, entry) => acc.add(entry.amount), new Decimal(0));

});

function getCaptureByPayment(payment: Models.Graph.Payment) {
  return payment.journalEntries.filter(r => r.orderId === state?.orderId).reduce((acc, entry) => entry.paymentJournalEntryType === 'charge' ? acc.add(entry.amount) : acc.sub(entry.amount), new Decimal(0));
}

const paymentCaptures = computed(() => {
  return state.payments.map(payment => {
    return {
      key: payment.paymentId,
      paymentNumber: payment.paymentNumber,
      amount: getCaptureByPayment(payment)
    }
  }).filter(r => !r.amount.eq(0));
});

const balance = computed(() => {
  return authorizedAmount.value.add(capturedAmount.value).sub(invoicedAmount.value);
});

const balanceWarning = computed(() => {
  return balance.value.lessThan(displayTotal.value.sub(invoicedAmount.value));
})


const displayInvoicing = computed(() => {
  return !authorizedAmount.value.eq(0) ||
    !invoicedAmount.value.eq(0) ||
    !capturedAmount.value.eq(0) ||
    !balance.value.eq(0);
});

</script>

<template>

  <div class="box-content box-pop p">
    <table class="order__sidebar__table">
      <tbody>
        <tr class="order__sidebar__record">
          <td class="order__sidebar__record-label">{{ t('products') }}</td>
          <td class="order__sidebar__record-value">{{ formatting.currency(productTotal, state.currencyCode, formatting.CurrencyDisplay.none) }}</td>
        </tr>
        <tr class="order__sidebar__record">
          <td class="order__sidebar__record-label">{{ t('shipping') }}</td>
          <td class="order__sidebar__record-value">{{ formatting.currency(shippingTotal, state.currencyCode, formatting.CurrencyDisplay.none) }}</td>
        </tr>
  
        <tr v-if="!state.taxIncluded" class="order__sidebar__record">
          <td class="order__sidebar__record-label">{{ taxLabel }}</td>
          <td class="order__sidebar__record-value">{{ formatting.currency(state.orderTaxTotal, state.currencyCode, formatting.CurrencyDisplay.none) }}</td>
        </tr>
        <tr class="order__sidebar__record order__sidebar__record--total">
          <td class="order__sidebar__record-label">{{ t('total') }}</td>
          <td class="order__sidebar__record-value">
            {{ formatting.currency(displayTotal, state.currencyCode, formatting.CurrencyDisplay.none) }}
          </td>
        </tr>
        <tr v-if="state.taxIncluded" class="order__sidebar__record order__sidebar__record--tax">
          <td class="order__sidebar__record-label">{{ taxLabel }}</td>
          <td class="order__sidebar__record-value">{{ formatting.currency(state.orderTaxTotal, state.currencyCode, formatting.CurrencyDisplay.none) }}</td>
        </tr>

      </tbody>
    </table>
  </div>

  <div v-if="displayInvoicing" class="box-content box-pop p">
    <table class="order__sidebar__table">
      <tbody>
        <tr class="order__sidebar__record" v-if="!authorizedAmount.eq(0)">
          <td class="order__sidebar__record-label">{{ t('authorized') }}</td>
          <td class="order__sidebar__record-value">{{ formatting.currency(authorizedAmount, state.currencyCode, formatting.CurrencyDisplay.none) }}</td>
        </tr>
        <tr class="order__sidebar__record" v-if="!invoicedAmount.eq(0)">
          <td class="order__sidebar__record-label">{{ t('invoiced') }}</td>
          <td class="order__sidebar__record-value">{{ formatting.currency(invoicedAmount, state.currencyCode, formatting.CurrencyDisplay.none) }}</td>
        </tr>
        <tr class="order__sidebar__record" v-if="paymentCaptures.length > 0">
          <td class="order__sidebar__record-label">{{ t('captured') }}</td>
          <td class="order__sidebar__record-value">{{ paymentCaptures.length > 1 ? formatting.currency(capturedAmount, state.currencyCode, formatting.CurrencyDisplay.none) : '&nbsp;' }}</td>
        </tr>
        <tr
          v-for="captures in paymentCaptures"
          :key="captures.key"
          class="order__sidebar__sub-record"
        >
          <td class="order__sidebar__record-label">{{ captures.paymentNumber }}</td>
          <td class="order__sidebar__record-value">{{ formatting.currency(captures.amount, state.currencyCode, formatting.CurrencyDisplay.none) }}</td>
        </tr>
        <tr
          class="order__sidebar__record"
          v-if="balanceWarning || !balance.eq(0)"
        >
          <td class="order__sidebar__record-label">{{ t('balance') }}</td>
          <td
            class="order__sidebar__record-value"
          >
            {{ formatting.currency(balance, state.currencyCode, formatting.CurrencyDisplay.none) }}
          </td>
        </tr>
        <tr v-if="balanceWarning" class="order__sidebar__record">
          <td class="order__sidebar__balance-warning">
            The available balance is not enough to cover for the order total. An additional {{ formatting.currency(displayTotal.sub(invoicedAmount).sub(balance), state.currencyCode, formatting.CurrencyDisplay.none) }} is required.

          </td>
          
        </tr>
      </tbody>
    </table>
  </div>
  

</template>

<style>
.order__sidebar__table {
  display: grid;
  grid-template-columns: 1fr min-content;
  white-space: nowrap;
}

.order__sidebar__table tbody {
  display: contents;
}

.order__sidebar__record,
.order__sidebar__sub-record {
  display: contents;
}

.order__sidebar__record-label {
  overflow: hidden;
  text-overflow: ellipsis;
}

.order__sidebar__sub-record {
  color: hsl(var(--color-30));
}

.order__sidebar__sub-record .order__sidebar__record-label {
  padding-left: 5px;
}

.order__sidebar__record-value {
  text-align: right;
}

.order__sidebar__record--total {
  font-weight: 600;
}

.order__sidebar__record--tax {
  color: hsl(var(--color-30));
}

.order__sidebar__balance-warning {
  grid-column: 1/3;
  white-space: normal;
  background-color: var(--color-warning);
  border-radius: 4px;
  color: #000;
  padding: 5px;
}
</style>